/**
 * Sidebar Navigation Item
 * Individual nav item with icon, label, and active state
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledListItemButton = styled(ListItemButton)(({ theme, selected, collapsed }) => ({
  borderRadius: 8,
  padding: collapsed ? '12px' : '10px 16px',
  marginBottom: 4,
  minHeight: 44,
  justifyContent: collapsed ? 'center' : 'flex-start',
  position: 'relative',
  transition: 'all 150ms ease',
  color: selected ? theme.palette.primary.dark : theme.palette.text.secondary,
  backgroundColor: selected ? `${theme.palette.primary.main}15` : 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: selected ? '60%' : 0,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '0 4px 4px 0',
    transition: 'height 150ms ease',
  },
  '&:hover': {
    backgroundColor: selected ? `${theme.palette.primary.main}20` : theme.palette.action.hover,
    '&::before': {
      height: '60%',
    },
  },
  '& .MuiListItemIcon-root': {
    minWidth: collapsed ? 0 : 40,
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    transition: 'color 150ms ease',
  },
  '& .MuiListItemText-root': {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 150ms ease',
    margin: 0,
  },
  '& svg path': {
    fill: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    transition: 'fill 150ms ease',
  },
  '&:hover .MuiListItemIcon-root': {
    color: theme.palette.primary.main,
  },
  '&:hover svg path': {
    fill: theme.palette.primary.main,
  },
}));

const SidebarNavItem = ({
  to,
  icon,
  label,
  collapsed = false,
  onClick,
  exact = false
}) => {
  const location = useLocation();
  const isSelected = exact
    ? location.pathname === to
    : location.pathname.startsWith(to);

  const content = (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <StyledListItemButton
        component={NavLink}
        to={to}
        selected={isSelected}
        collapsed={collapsed ? 1 : 0}
        onClick={onClick}
      >
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: '0.875rem',
                }}
              >
                {label}
              </Typography>
            }
          />
        )}
      </StyledListItemButton>
    </ListItem>
  );

  if (collapsed) {
    return (
      <Tooltip title={label} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default SidebarNavItem;
